import React, { type ReactNode } from "react";

interface ProfileHeaderLayoutProps {
  cover: ReactNode;
  avatar: ReactNode;
  name: ReactNode;
  address: ReactNode;
  status: ReactNode;
  relation?: ReactNode;
  follow: ReactNode;
  action?: ReactNode;
  ranking: ReactNode;
  loading?: boolean;
}

/** Keep loading and loaded headers in the same responsive layout. */
export default function ProfileHeaderLayout({
  cover,
  avatar,
  name,
  address,
  status,
  relation,
  follow,
  action,
  ranking,
  loading,
}: ProfileHeaderLayoutProps): React.ReactElement {
  return (
    <div className="w-full mx-auto md:px-8 xl:max-w-(--breakpoint-xl) xl:pt-8 print:max-w-(--breakpoint-lg) print:mt-8">
      <div className="grid grid-cols-1 xl:grid-cols-[30%_minmax(0,1fr)] xl:grid-rows-[auto_1fr] xl:shadow-common xl:rounded-2xl print:grid-cols-[35%_minmax(0,1fr)] print:grid-rows-[auto_1fr] print:shadow-common print:rounded-2xl">
        <div className="min-w-0 xl:col-start-2 xl:row-start-1 print:col-start-2 print:row-start-1">
          {cover}
        </div>
        <div
          data-testid={
            loading ? "profile-skeleton-identity" : "profile-identity"
          }
          className="flex items-center justify-center bg-gray-200 px-4 py-6 md:py-4 xl:px-6 xl:py-8 xl:rounded-l-2xl xl:text-center xl:col-start-1 xl:row-start-1 xl:row-span-2 print:px-6 print:py-5 print:rounded-l-2xl print:text-center print:col-start-1 print:row-start-1 print:row-span-2"
        >
          <div className="w-full grid grid-flow-row space-y-4 md:flex md:justify-between md:items-center md:space-x-6 xl:block xl:space-x-0 xl:space-y-2 print:block print:space-x-0 print:space-y-2">
            <div>
              <div className="flex justify-start gap-4 xl:flex-col xl:items-center xl:gap-2 print:flex-col print:items-center print:gap-2">
                <div className="shrink-0 xl:pb-2 print:pb-2">{avatar}</div>
                <div className="flex flex-col gap-2 xl:w-full print:w-full">
                  {name}
                  {address}
                </div>
              </div>
              <div className="w-full flex flex-col lg:items-center justify-start pb-2 xl:pb-0 xl:mt-2 xl:space-y-2 print:space-y-2 print:pb-0 print:mt-2">
                {/* Reserve one status line; longer descriptions can still grow naturally. */}
                <div className="min-h-11 lg:min-h-9">{status}</div>
                <div>
                  {relation}
                  {follow}
                </div>
              </div>
            </div>
            {action}
          </div>
        </div>
        <div
          data-testid={loading ? "profile-skeleton-ranking" : "profile-ranking"}
          className="flex min-w-0 xl:col-start-2 xl:row-start-2 print:col-start-2 print:row-start-2"
        >
          {ranking}
        </div>
      </div>
    </div>
  );
}
